import React, { useState, useEffect } from 'react';
import { Check, Loader, Crown, ArrowRight, Star, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { useUserPlan } from '../hooks/useUserPlan';
import { useAnalytics } from '../hooks/useAnalytics';
import SEOHead from '../components/SEOHead';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const testimonials = [
  {
    name: 'A5omic (Ryan)',
    handle: '@A5omic',
    quote: 'Built this for my own content because nothing else could keep up. Pro is the only reason I still have free weekends.',
    stats: 'Solo dev · 5 uploads/day'
  },
  {
    name: 'ClipThis Community',
    handle: '@clipthis_io',
    quote: 'Creators inside the beta Slack keep telling me premium “feels like cheating.” That’s the goal.',
    stats: 'Community feedback · weekly drops'
  },
  {
    name: 'Indie Creator Fam',
    handle: '@clipthis_users',
    quote: 'From Twitch partners to small agencies—everyone asked for faster queues and no watermarks. Pro delivers both.',
    stats: 'Mixed roles · 14+ teams'
  }
];

const Pricing: React.FC = () => {
  const [loading, setLoading] = useState<string | null>(null);
  const { currentUser } = useAuth();
  const { planData } = useUserPlan();
  const { trackSubscriptionPlanViewed, trackSubscriptionStarted, trackSubscriptionCheckoutAbandoned } = useAnalytics();
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    // Use smooth scroll with a slight delay to ensure page is rendered
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  }, []);

  const handleUpgrade = async (planName: string) => {
    if (planName === 'free') {
      return;
    }

    if (!currentUser) {
      toast.error('Please sign in to upgrade your plan');
      return;
    }

    setLoading(planName);
    
    // Track plan selection and upgrade start
    const planPrices: Record<string, number> = {
      'starter_plus': 9.99,
      'creator': 19.99,
      'pro': 39.99,
      'lifetime': 299
    };
    const planPrice = planPrices[planName] || 0;
    trackSubscriptionPlanViewed(planName, planPrice);
    trackSubscriptionStarted(planName, planPrice);
    
    try {
      const token = await currentUser.getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/subscription/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan: planName,
          success_url: `${window.location.origin}/dashboard?upgrade=success`,
          cancel_url: `${window.location.origin}/pricing?upgrade=cancelled`
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      
      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;
      
    } catch (error) {
      console.error('Error creating checkout session:', error);
      trackSubscriptionCheckoutAbandoned(planName, 'checkout_creation_failed');
      toast.error('Failed to start checkout process. Please try again.');
      setLoading(null);
    }
  };

  const plans = [
    {
      name: 'Free',
      displayName: 'Starter',
      price: '$0',
      compareAt: null,
      period: 'forever',
      description: 'Perfect for testing the waters',
      videoLimit: '3 videos per month',
      storage: '1GB storage',
      features: [
        '3 videos per month',
        'Watermark on exports',
        '720p max quality',
        'Basic AI analysis',
        'Community support'
      ],
      buttonText: 'Get Started Free',
      buttonAction: () => handleUpgrade('free'),
      popular: false,
      disabled: false
    },
    {
      name: 'Starter_Plus',
      displayName: 'Starter Plus',
      price: '$9.99',
      compareAt: '$14.99',
      period: '/month',
      description: 'Great for casual creators',
      videoLimit: '10 videos/month',
      storage: '5GB storage',
      features: [
        '10 videos per month',
        'No watermarks ⭐',
        'All export formats',
        'Clip conversion (horizontal to landscape) ⭐',
        'YouTube downloader (MP3 & MP4) ⭐',
        'Email support',
        'Basic AI analysis'
      ],
      buttonText: 'Get Started',
      buttonAction: () => handleUpgrade('starter_plus'),
      popular: true,
      disabled: false
    },
    // {
    //   name: 'Creator',
    //   displayName: 'Creator',
    //   price: '$19.99',
    //   period: '/month',
    //   description: 'Perfect for growing creators',
    //   videoLimit: '20 videos/month',
    //   storage: '15GB storage',
    //   features: [
    //     '20 videos per month',
    //     'No watermarks ⭐',
    //     'Priority processing',
    //     'All export formats',
    //     'Clip conversion (horizontal to landscape) ⭐',
    //     'YouTube downloader (MP3 & MP4) ⭐',
    //     'Priority email support',
    //     'Advanced AI analysis'
    //   ],
    //   buttonText: 'Start Creating',
    //   buttonAction: () => handleUpgrade('creator'),
    //   popular: true,
    //   disabled: false
    // },
    {
      name: 'Pro',
      displayName: 'Pro',
      price: '$39.99',
      compareAt: '$59.99',
      period: '/month',
      description: 'For serious content creators',
      videoLimit: '50 videos/month',
      storage: '50GB storage',
      features: [
        '50 videos per month',
        'No watermarks ⭐',
        'Priority processing (2x faster)',
        'All export formats',
        'Clip conversion (horizontal to landscape) ⭐',
        'YouTube downloader (MP3 & MP4) ⭐',
        'Priority email support',
        'Advanced AI analysis',
        'Custom clip lengths',
        'API access (coming soon)'
      ],
      buttonText: 'Go Pro',
      buttonAction: () => handleUpgrade('pro'),
      popular: false,
      disabled: false
    },
    {
      name: 'Lifetime',
      displayName: 'Lifetime Access',
      price: '$299',
      compareAt: '$499',
      period: 'one-time',
      description: 'Pay once, use forever',
      videoLimit: '50 videos/month',
      storage: '50GB storage',
      features: [
        '50 videos per month',
        'No watermarks ⭐',
        'Priority processing (2x faster)',
        'All export formats',
        'Clip conversion (horizontal to landscape) ⭐',
        'YouTube downloader (MP3 & MP4) ⭐',
        'Priority email support',
        'Advanced AI analysis',
        'Custom clip lengths',
        'API access (coming soon)',
        'Lifetime access - No recurring fees! 🎉'
      ],
      buttonText: 'Get Lifetime Access',
      buttonAction: () => handleUpgrade('lifetime'),
      popular: false,
      disabled: false
    }
  ];

  // Check if user already has an active subscription
  const hasActiveSubscription = planData?.has_active_subscription;
  const currentPlan = planData?.plan;

  return (
    <>
      <SEOHead 
        title="Pricing - ClipThis"
        description="Choose the perfect plan for your content creation needs. Professional tools for YouTube, Twitch, and social media creators."
        keywords="video editing pricing, content creator tools, YouTube video editor, social media content"
      />
      
      <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 relative" style={{ backgroundColor: '#ffffff' }}>
        {/* Subtle animated background */}
        <div className="absolute inset-0 dot-pattern-animated opacity-30 pointer-events-none" style={{ zIndex: 0 }} />
        
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Sale Banner */}
          <div className="mb-10">
            <div className="bg-gradient-to-r from-red-700 via-red-600 to-red-500 rounded-3xl shadow-xl border border-red-200 text-white p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div>
                <p className="text-xs uppercase tracking-[0.4em] text-white/70">Early adopter sale</p>
                <h2 className="text-2xl sm:text-3xl font-display font-bold mt-2 mb-2">Save 40% before prices increase</h2>
                <p className="text-white/80 text-sm max-w-2xl">
                  ClipThis is still indie-built, so I’m keeping beta pricing live a little longer. Lock it in permanently today—future price bumps won’t affect you.
                </p>
              </div>
              <div className="bg-white/10 border border-white/30 rounded-2xl px-6 py-4 text-center">
                <p className="text-xs uppercase tracking-[0.3em] text-white/80 mb-1">Sale ends in</p>
                <p className="text-3xl font-bold">02<span className="text-base align-top ml-1">d</span> 18<span className="text-base align-top ml-1">h</span></p>
                <p className="text-xs text-white/70 mt-1">Auto-renews at locked-in rate</p>
              </div>
            </div>
          </div>

          {/* Back Button */}
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 transition-colors mb-8"
          >
            <ArrowLeft size={20} />
            <span>Back</span>
          </button>
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Choose Your Plan
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Built by a solo developer who uses this daily. Join early adopters getting in at beta pricing.
            </p>
            
            {/* Honest metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 max-w-4xl mx-auto">
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 hover:border-red-300 transition-colors">
                <div className="text-3xl font-bold text-gray-900 mb-2">⚡</div>
                <div className="text-gray-600 font-semibold">10x Faster</div>
                <div className="text-sm text-gray-500 mt-1">Than manual editing</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 hover:border-red-300 transition-colors">
                <div className="text-3xl font-bold text-gray-900 mb-2">🎯</div>
                <div className="text-gray-600 font-semibold">AI-Powered</div>
                <div className="text-sm text-gray-500 mt-1">Smart highlight detection</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 hover:border-red-300 transition-colors">
                <div className="text-3xl font-bold text-gray-900 mb-2">🚀</div>
                <div className="text-gray-600 font-semibold">Indie Made</div>
                <div className="text-sm text-gray-500 mt-1">Built by creators, for creators</div>
              </div>
            </div>
          </div>

          {/* Current subscription notice */}
          {hasActiveSubscription && (
            <div className="max-w-4xl mx-auto mb-8">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <Check className="w-5 h-5 text-red-500 mr-2" />
                  <span className="text-red-800 font-medium">
                    You currently have an active {currentPlan} subscription
                  </span>
                </div>
              </div>
            </div>
          )}
          
          {/* Pricing Toggle - Monthly vs Lifetime */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex items-center bg-gray-100 rounded-full p-1">
              <button className="px-6 py-2 rounded-full bg-white text-gray-900 font-medium shadow-sm">
                Monthly Plans
              </button>
              <button className="px-6 py-2 rounded-full text-gray-600 font-medium">
                Lifetime Deal
              </button>
            </div>
          </div>

          {/* Monthly Plans - Simplified 3-Column Grid */}
          <div className="max-w-5xl mx-auto mb-12">
            {/* Monthly Subscription Plans */}
            <div className="grid md:grid-cols-3 gap-8 mb-8 justify-items-center">
              {plans.filter(p => p.name !== 'Lifetime').map((plan, index) => (
                <div 
                  key={index}
                  className={`relative flex flex-col rounded-2xl border-2 transition-all w-full max-w-sm ${
                    plan.popular 
                      ? 'border-red-500 bg-white shadow-2xl ring-4 ring-red-100' 
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-lg'
                  }`}
                >
                  {plan.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                        <span className="bg-red-600 text-white px-4 py-1 rounded-full text-xs font-bold flex items-center shadow-lg">
                          <Crown className="w-3 h-3 mr-1" />
                          MOST POPULAR
                        </span>
                      </div>
                  )}
                  
                  <div className="p-6 flex-1">
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-bold mb-2 text-gray-900">{plan.displayName}</h3>
                      <div className="mb-2">
                        {plan.compareAt && (
                          <span className="text-sm text-gray-400 line-through mr-2">{plan.compareAt}</span>
                        )}
                        <span className="text-3xl font-bold text-gray-900">{plan.price}</span>
                        <span className="text-gray-500 text-sm">{plan.period}</span>
                      </div>
                      <p className="text-sm text-gray-600">{plan.description}</p>
                    </div>
                    
                    <div className="space-y-4 mb-6">
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <div className="font-bold text-gray-900">{plan.videoLimit}</div>
                        <div className="text-xs text-gray-600">{plan.storage}</div>
                      </div>
                      
                      <ul className="space-y-2">
                        {plan.features.slice(0, 4).map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-start text-sm">
                            <Check size={16} className="text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                        {plan.features.length > 4 && (
                          <li className="text-xs text-gray-500 pl-6">
                            +{plan.features.length - 4} more features
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                  
                  <div className="p-6 pt-0">
                    <button 
                      onClick={plan.buttonAction}
                      disabled={loading === plan.name.toLowerCase() || plan.disabled}
                      className={`w-full py-3 px-4 rounded-xl font-semibold transition-all flex items-center justify-center ${
                        plan.popular 
                          ? 'bg-red-600 text-white hover:bg-red-700 shadow-lg' 
                          : plan.name === 'Free'
                          ? 'bg-gray-100 text-gray-900 hover:bg-gray-200 border-2 border-gray-300'
                          : 'bg-gray-900 text-white hover:bg-gray-800'
                      } ${(loading === plan.name.toLowerCase() || plan.disabled) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {loading === plan.name.toLowerCase() ? (
                        <div className="flex items-center">
                          <Loader size={16} className="animate-spin mr-2" />
                          Processing...
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <span>{plan.buttonText}</span>
                          {plan.name !== 'Free' && <ArrowRight size={16} className="ml-2" />}
                        </div>
                      )}
                    </button>

                    {hasActiveSubscription && currentPlan === plan.name.toLowerCase() && (
                      <div className="mt-3 text-center">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <Check size={12} className="mr-1" />
                          Current Plan
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Lifetime Plan - Featured Separately */}
            {plans.filter(p => p.name === 'Lifetime').map((plan, index) => (
              <div key={index} className="relative">
                {/* Best Value Badge */}
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                  <span className="bg-red-500 text-white px-6 py-2 rounded-full text-sm font-bold flex items-center shadow-xl">
                    <Star className="w-4 h-4 mr-1 fill-white" />
                    BEST VALUE - LIMITED TIME
                  </span>
                </div>

                <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black rounded-2xl border-2 border-red-500 shadow-2xl overflow-hidden">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-red-500/10 to-transparent rounded-full blur-3xl"></div>
                  
                  <div className="relative p-8 md:p-10">
                    <div className="grid md:grid-cols-2 gap-8 items-center">
                      {/* Left Side - Plan Info */}
                      <div>
                        <div className="inline-block px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-bold mb-4">
                          ONE-TIME PAYMENT
                        </div>
                        <h3 className="text-3xl md:text-4xl font-bold mb-3 text-white">{plan.displayName}</h3>
                        <div className="mb-4">
                          {plan.compareAt && (
                            <span className="text-gray-400 text-lg line-through mr-3">{plan.compareAt}</span>
                          )}
                          <span className="text-5xl md:text-6xl font-bold text-white">{plan.price}</span>
                          <span className="text-gray-400 text-xl ml-2">{plan.period}</span>
                        </div>
                        <p className="text-gray-300 text-lg mb-6">{plan.description}</p>
                        
                        <div className="flex items-center space-x-4 mb-6">
                          <div className="bg-white/10 backdrop-blur rounded-lg p-4 flex-1">
                            <div className="font-bold text-white text-lg">{plan.videoLimit}</div>
                            <div className="text-xs text-gray-400">{plan.storage}</div>
                          </div>
                          <div className="bg-white/10 backdrop-blur rounded-lg p-4 flex-1">
                            <div className="font-bold text-red-400 text-lg">Forever</div>
                            <div className="text-xs text-gray-400">No recurring fees</div>
                          </div>
                        </div>

                        <button 
                          onClick={plan.buttonAction}
                          disabled={loading === plan.name.toLowerCase()}
                          className="w-full bg-red-500 text-white py-4 px-8 rounded-xl font-bold text-lg hover:bg-red-600 transition-all shadow-xl hover:shadow-2xl flex items-center justify-center disabled:opacity-50"
                        >
                          {loading === plan.name.toLowerCase() ? (
                            <div className="flex items-center">
                              <Loader size={20} className="animate-spin mr-2" />
                              Processing...
                            </div>
                          ) : (
                            <div className="flex items-center">
                              <span>{plan.buttonText}</span>
                              <ArrowRight size={20} className="ml-2" />
                            </div>
                          )}
                        </button>

                        {hasActiveSubscription && currentPlan === plan.name.toLowerCase() && (
                          <div className="mt-4 text-center">
                            <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-gray-200 text-gray-800 border border-gray-300">
                              <Check size={14} className="mr-1" />
                              Active - Lifetime Access
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Right Side - Features */}
                      <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
                        <h4 className="text-white font-bold mb-4 flex items-center">
                          <Check className="w-5 h-5 text-red-400 mr-2" />
                          Everything Included:
                        </h4>
                        <ul className="space-y-3">
                          {plan.features.map((feature, featureIndex) => (
                            <li key={featureIndex} className="flex items-start">
                              <Check size={18} className="text-red-400 mr-3 flex-shrink-0 mt-0.5" />
                              <span className="text-gray-300">{feature}</span>
                            </li>
                          ))}
                        </ul>
                        
                        <div className="mt-6 pt-6 border-t border-white/10">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-red-400 mb-1">Save $180/year</div>
                            <div className="text-sm text-gray-400">vs. monthly Pro plan</div>
                            <div className="mt-3 bg-red-500/10 border border-red-500/30 rounded-lg p-2">
                              <div className="text-xs text-red-500 font-bold">⚡ 7 people bought this week</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Why ClipThis Section */}
          <div className="max-w-6xl mx-auto mb-12">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-gray-900 mb-2">Built by a Creator, for Creators</h3>
              <p className="text-gray-600">I got tired of spending hours editing highlights, so I built this</p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                <div className="text-4xl mb-4">⚡</div>
                <h4 className="font-bold text-gray-900 mb-2">Save 10+ Hours Weekly</h4>
                <p className="text-gray-700 text-sm">What used to take me hours of manual editing now takes minutes. I use this for my own content every single day.</p>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                <div className="text-4xl mb-4">🎯</div>
                <h4 className="font-bold text-gray-900 mb-2">Actually Works</h4>
                <p className="text-gray-700 text-sm">The AI finds the good moments - the reactions, the plays, the funny bits. It's not perfect, but it's way better than scrubbing through hours of footage.</p>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                <div className="text-4xl mb-4">🤝</div>
                <h4 className="font-bold text-gray-900 mb-2">Indie & Honest</h4>
                <p className="text-gray-700 text-sm">No VC funding, no fake testimonials. Just a solo dev building something useful. Your feedback directly shapes the product.</p>
              </div>
            </div>
          </div>

          {/* Trust signals and guarantees */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-50 rounded-2xl p-8 mb-12">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Why Choose ClipThis?</h3>
              </div>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="flex items-start">
                    <Star className="w-5 h-5 text-red-500 mr-3 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">AI-Powered Editing</h4>
                      <p className="text-gray-600 text-sm">Advanced AI automatically finds the best moments in your videos</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Star className="w-5 h-5 text-red-500 mr-3 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Multi-Platform Support</h4>
                      <p className="text-gray-600 text-sm">Works with YouTube, Twitch, Kick, and more</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Star className="w-5 h-5 text-red-500 mr-3 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Professional Quality</h4>
                      <p className="text-gray-600 text-sm">Export in multiple formats with no quality loss</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start">
                    <Star className="w-5 h-5 text-red-500 mr-3 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Fast Processing</h4>
                      <p className="text-gray-600 text-sm">Cloud-based processing for lightning-fast results</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Star className="w-5 h-5 text-red-500 mr-3 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">24/7 Support</h4>
                      <p className="text-gray-600 text-sm">Get help whenever you need it</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Star className="w-5 h-5 text-gray-500 mr-3 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Cancel Anytime</h4>
                      <p className="text-gray-600 text-sm">No long-term commitments or hidden fees</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonials */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 mb-12">
              <div className="text-center mb-8">
                <p className="text-xs uppercase tracking-[0.4em] text-red-500 mb-2">Trusted by creators</p>
                <h3 className="text-3xl font-bold text-gray-900">What Pro users are saying</h3>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {testimonials.map((testimonial, index) => (
                  <div key={index} className="border border-gray-200 rounded-xl p-5 bg-white">
                    <p className="text-sm text-gray-600 mb-4 leading-relaxed">&ldquo;{testimonial.quote}&rdquo;</p>
                    <div className="mt-4">
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-xs uppercase tracking-wide text-red-500">{testimonial.handle}</p>
                      <p className="text-xs text-gray-500 mt-2">{testimonial.stats}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Trust signals */}
            <div className="text-center border-t pt-8">
              <div className="flex flex-wrap justify-center items-center gap-6 text-gray-600 mb-4">
                <span className="flex items-center">
                  <Check size={16} className="text-red-500 mr-2" />
                  Secure payments via Stripe
                </span>
                <span className="flex items-center">
                  <Check size={16} className="text-red-500 mr-2" />
                  Cancel anytime, no questions
                </span>
                <span className="flex items-center">
                  <Check size={16} className="text-red-500 mr-2" />
                  30-day money back guarantee
                </span>
              </div>
              <p className="text-sm text-gray-500 mb-2">
                Early adopter pricing. Support an indie developer building tools for creators.
              </p>
              <div className="flex justify-center items-center space-x-2 text-xs text-gray-400">
                <span>🔒 SSL Secured</span>
                <span>•</span>
                <span>💳 Stripe Payments</span>
                <span>•</span>
                <span>🚀 Indie Made</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Pricing;
